import { type FC, useState } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { Alert, Button, IconButton } from '@mui/joy';

import { type AccessKeyActionFormikValues } from '../hooks/useAccessKeyActionFormikConfig';
import { type AvailableAccessKeyInstance } from '@/types/trpc/accessKeys';
import { type UseToggle } from '@/shared/hooks/UseToggle';

import { CancelIcon, SaveIcon, WarningIcon } from '@/shared/icons/Icons';
import AccessKeyActionSelectedInstancesAlertList from './AccessKeyActionSelectedInstancesAlertList';
import AddAccessKeyActionAvailableAccessKeysSection from './AddAccessKeyActionAvailableAccessKeysSection';
import KpcModal from '@/shared/components/2.0/layout/KpcModal';

type Props = {
  toggle: UseToggle;
  selectedAccessKeyInstanceId?: string;
  actionGroupId?: string;
};

const AddAccessKeyActionSelectAccessKeysModal: FC<Props> = ({
  toggle,
  selectedAccessKeyInstanceId,
  actionGroupId,
}) => {
  const { t } = useTranslation();
  const [lastInstanceResolutionHadNoMatches, setLastInstanceResolutionHadNoMatches] =
    useState(false);
  const [instanceNotFoundAlertDismissed, setInstanceNotFoundAlertDismissed] = useState(false);
  const showInstanceNotFoundAlert =
    lastInstanceResolutionHadNoMatches && !instanceNotFoundAlertDismissed;
  const { values, setFieldValue } = useFormikContext<AccessKeyActionFormikValues>();

  const [selectedAccessKeys, setSelectedAccessKeys] = useState<AvailableAccessKeyInstance[]>(
    values.accessKeys,
  );

  const handleSave = () => {
    const shouldInform = selectedAccessKeys.every(
      (accessKey) => accessKey.informCoOwnerWhenLending,
    );
    setFieldValue('accessKeys', selectedAccessKeys);
    setFieldValue('shouldInformCoOwner', shouldInform);
    setFieldValue('shouldInformBorrower', shouldInform);
    toggle.hide();
  };

  return (
    <KpcModal className="w-3/5" toggle={toggle} title={t('_accessKeyActions.selectAccessKeys')}>
      <div className="my-1 flex flex-col gap-4">
        {showInstanceNotFoundAlert && (
          <Alert
            color="danger"
            variant="soft"
            startDecorator={<WarningIcon />}
            endDecorator={
              <IconButton
                className="rounded-full"
                variant="plain"
                color="neutral"
                onClick={() => setInstanceNotFoundAlertDismissed(true)}
              >
                <CancelIcon />
              </IconButton>
            }
          >
            {t('_accessKeyActions.selectedAccessKeyInstanceNotAvailable')}
          </Alert>
        )}
        <AccessKeyActionSelectedInstancesAlertList
          keys={selectedAccessKeys}
          onRemove={(accessKeyInstanceId) =>
            setSelectedAccessKeys((prev) =>
              prev.filter((ak) => ak.accessKeyInstanceId !== accessKeyInstanceId),
            )
          }
        />
        <AddAccessKeyActionAvailableAccessKeysSection
          actionGroupId={actionGroupId}
          actionType={values.actionType}
          selectedAccessKeyInstanceId={selectedAccessKeyInstanceId}
          selectedAccessKeys={selectedAccessKeys}
          setInstanceNotFoundAlertDismissed={setInstanceNotFoundAlertDismissed}
          setLastInstanceResolutionHadNoMatches={setLastInstanceResolutionHadNoMatches}
          setSelectedAccessKeys={setSelectedAccessKeys}
        />
        <div className="flex flex-row justify-end gap-4">
          <Button
            onClick={() => toggle.hide()}
            color="neutral"
            variant="outlined"
            startDecorator={<CancelIcon />}
          >
            {t('cancel')}
          </Button>
          <Button onClick={handleSave} startDecorator={<SaveIcon />}>
            {t('save')}
          </Button>
        </div>
      </div>
    </KpcModal>
  );
};

export default AddAccessKeyActionSelectAccessKeysModal;
